import * as React from 'react';
import Box from '@mui/material/Box';
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid';
import { Lottery, PrizeWrapper } from 'src/util/types';

import { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Modal, Typography, CircularProgress } from '@mui/material';
import axios from 'axios';
import config from 'src/config';
import { useAuth } from 'src/util/authenticationProvider';
import LotteryEdit from '../LotteryEdit';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';

const formatDate = (value) => {
  return new Date(value).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatPrizes = (value: PrizeWrapper[] | Record<string, PrizeWrapper> = []) => {
  const list = Array.isArray(value) ? value : Object.values(value ?? {});
  return list
    .map((wrapper) => {
      const description = wrapper?.prize?.description ?? '';
      const amount = wrapper?.amount ?? 0;
      return `${description}${amount ? ` (x${amount})` : ''}`;
    })
    .filter(Boolean)
    .join(' | ');
};

const columns: GridColDef<Lottery>[] = [
  {
    field: 'name',
    headerName: t('lottery.name'),
    width: 250,
  },
  {
    field: 'startDate',
    headerName: t('lottery.startDate'),
    width: 170,
    valueFormatter: value =>
      formatDate(value),
  },
  {
    field: 'endDate',
    headerName: t('lottery.endDate'),
    width: 170,
    valueFormatter: value =>
      formatDate(value),
  },
  {
    field: 'active',
    headerName: t('lottery.active'),
    width: 100,
  },
  {
    field: 'closed',
    headerName: t('lottery.closed'),
    width: 100,
  },
  {
    field: 'prizes',
    headerName: t('lottery.prizes'),
    width: 270,
    valueFormatter: (value: PrizeWrapper[] | Record<string, PrizeWrapper>) => formatPrizes(value)
  },
];



export default function LotteriesList({
  rows,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  paginationMode,
  loading
}) {
  const [selectedRow, setSelectedRow] = useState(null);
  const [open, setOpen] = React.useState(false);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const navigate=useNavigate();
  const {t} = useTranslation();
  const authContext = useAuth();

  const deleteLottery = () => {
    axios.delete(config.apiUrl + '/api/lottery/delete/' + selectedRow.id, {
      headers: {
        'Authorization': 'Bearer ' + authContext.tokens.accessToken
      }
    })
      .then(res => {
        if (res.data.status >= 200) {
          setOpenDialog(false)
        }
      })
      .catch(error=>{
        if (error.response?.status === 401) {
          authContext.logout();
          navigate('/cp/login');
        }
      });
  }

  
  const handleOpen = () => {
    setSuccessMessage(null);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);

  const style = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
    maxHeight: '90vh',
    overflowY: 'auto',
    width: '90vw',
    maxWidth: 900,
  };

  return (
    <Box sx={{ width: '100%', backgroundColor: '#fff' }}>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            {t('lottery.editTitle')}
          </Typography>
          <Box id="modal-modal-description" sx={{ mt: 2 }}>
            <LotteryEdit
              {...selectedRow}
              onSuccess={(msg) => {
                setOpen(false);
                setSuccessMessage(msg || t('lottery.editTitle'));
              }}
            />
          </Box>
        </Box>
      </Modal>
      <Button disabled={!selectedRow} onClick={handleOpen}>{t('general.Edit')}</Button>
      <Button disabled={!selectedRow} onClick={handleOpenDialog}>{t('general.Delete')}</Button>
      <Dialog
        open={openDialog}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('lottery.deleteTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('lottery.deleteConfirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('general.No')}</Button>
          <Button onClick={deleteLottery} autoFocus>
            {t('general.Yes')}
          </Button>
        </DialogActions>
      </Dialog>

      {successMessage && (
        <DialogContentText sx={{ color: 'success.main', mb: 2 }}>{successMessage}</DialogContentText>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          paginationMode={paginationMode}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: paginationModel.pageSize,
              },
            },
          }}
          isRowSelectable={(params: GridRowParams) => params.row.closed == false}
          pageSizeOptions={[paginationModel.pageSize]}
          checkboxSelection
          disableMultipleRowSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={(ids) => {
            const selectedIDs = new Set(ids);
            const selectedRowData = rows.filter((row) =>
              selectedIDs.has(row.id.toString())
            );
            setSelectedRow(selectedRowData[0]);
          }}
        />
      )}
    </Box>
  );
}
